/**
 * DataTable — a compact, HomeLyph Pro-styled table for Family B pages.
 *
 * Rows follow the pattern:
 *   [Icon/Pill] [Primary + secondary text] [Middle value] [ProgressRing] [Action]
 *
 * Usage:
 *   <DataTable
 *      columns={[{key:'label'}, {key:'value', align:'right'}, {key:'progress'}, {key:'action'}]}
 *      rows={[
 *        { icon: ChefHat, label: 'Strawberries', sub: '3.2 kg', value: '$42', progress: 65, action: <Button>View</Button> },
 *        ...
 *      ]}
 *   />
 */
import React from 'react';
import { ProgressRing } from './ProgressRing';

const DataTable = ({ rows = [], emptyMessage = 'Nothing to show yet.', testId = 'data-table' }) => {
  if (!rows.length) {
    return (
      <div className="rounded-2xl border border-border bg-card p-6 text-center" data-testid={`${testId}-empty`}>
        <p className="text-sm text-muted-foreground italic">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid={testId}>
      <ul className="divide-y divide-border">
        {rows.map((r, i) => {
          const Icon = r.icon;
          return (
            <li
              key={r.id || i}
              className="grid items-center gap-4 px-5 py-4 hover:bg-muted/40 transition-colors"
              style={{ gridTemplateColumns: 'auto minmax(0, 1fr) auto auto auto' }}
              data-testid={`data-table-row-${i}`}
            >
              <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                {Icon ? <Icon className="w-4 h-4" /> : <span className="text-[10px] uppercase tracking-widest font-bold">{(r.label || '?').charAt(0)}</span>}
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-foreground truncate">{r.label}</div>
                {r.sub && <div className="text-xs text-muted-foreground truncate">{r.sub}</div>}
              </div>
              <div className="hidden sm:block font-heading font-extrabold text-base text-foreground text-right whitespace-nowrap">
                {r.value ?? ''}
              </div>
              <div className="hidden sm:flex">
                {typeof r.progress === 'number'
                  ? <ProgressRing value={r.progress} size={40} stroke={4} />
                  : <div style={{ width: 40 }} />}
              </div>
              <div className="flex justify-end">{r.action}</div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default DataTable;
